import React, {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {RouteDefine} from "../../../../routes/RouteDefine";
import {getSettingsEconomicPath} from "../../../../routes/settingsPaths";
import QLoading from "../../../../common/components/QLoading";
import BusinessApi from "../../../../common/api/functions/Business";
import SetupApi from "../../../../common/api/functions/Setup";
import ScheduleApi from "../../../../common/api/functions/Schedule";
import {ResponseInterface} from "../../../../interfaces/ResponseInterface";
import {config} from "../../../../config";
import {BusinessInterface} from "../../../../interfaces/private/BusinessInterface";
import {SetupInterface} from "../../../../interfaces/private/SetupInterface";
import {ScheduleInterface} from "../../../../interfaces/private/ScheduleInterface";
import {structPagination} from "../../../../interfaces/PaginationInterface";
import {getSyncEngineVersion} from "../../../../common/helpers/syncEngine";
import {
    getIncompleteEnabledSections,
    getSchedulableSyncModules,
    SetupSectionKey,
} from "../../../../common/helpers/setupCompleteness";
import {SYNC_NAV_ITEMS} from "../../../../common/helpers/syncNavModules";
import SettingsPageHeader from "../components/SettingsPageHeader";
import SettingsNavCard from "../components/SettingsNavCard";

const SettingsOverview: React.FC = () => {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(true);
    const [business, setBusiness] = useState<BusinessInterface>({
        address: undefined,
        city: undefined,
        company_name: undefined,
        country: undefined,
        created_at: undefined,
        cvr_no: undefined,
        email: undefined,
        id: undefined,
        name: undefined,
        phone: undefined,
        updated_at: undefined,
        website: undefined,
        zip: undefined,
        password: undefined,
    });
    const [setup, setSetup] = useState<SetupInterface>({});
    const [schedules, setSchedules] = useState<ScheduleInterface[]>([]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const [businessRes, setupRes, scheduleRes] = await Promise.all([
                    BusinessApi.detail(),
                    SetupApi.detail(),
                    ScheduleApi.getAll({...structPagination, per_page: 50, page: 1}),
                ]);

                if (cancelled) {
                    return;
                }

                if ((businessRes as ResponseInterface)?.status === config.STATUS_CODE.OK) {
                    setBusiness((businessRes as ResponseInterface)?.data?.data || {});
                }

                if ((setupRes as ResponseInterface)?.status === config.STATUS_CODE.OK) {
                    setSetup((setupRes as ResponseInterface)?.data?.data || {});
                }

                if ((scheduleRes as ResponseInterface)?.status === config.STATUS_CODE.OK) {
                    setSchedules((scheduleRes as ResponseInterface)?.data?.data?.data || []);
                }
            } catch {
                // ignore — overview still renders with partial data
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, []);

    const profileComplete = Boolean(business.email && (business.company_name || business.name));
    const stripeConnected = Boolean(setup.stripe_access_token);
    const economicConnected = Boolean(setup.economic_token);
    const isV2 = getSyncEngineVersion() === "v2";
    const incompleteSections = isV2 ? getIncompleteEnabledSections(setup) : [];
    const enabledModules = isV2 ? getSchedulableSyncModules(setup) : [];
    const scheduleCount = schedules.length;

    const sectionLabel = (key: SetupSectionKey) => {
        const map: Record<SetupSectionKey, string> = {
            invoice: t("economic.sectionInvoice"),
            refund: t("economic.sectionRefund"),
            payment: t("economic.sectionPayment"),
            payout: t("economic.sectionPayout"),
            subscription: t("economic.sectionSubscription"),
            common: t("economic.sectionCommon"),
        };
        return map[key] || key;
    };

    const moduleLabel = (key: string) => {
        const item = SYNC_NAV_ITEMS.find((i) => i.key === key);
        return item ? t(item.labelKey) : key;
    };

    const missingScheduleModules = useMemo(() => {
        if (!isV2) {
            return [] as string[];
        }
        const scheduled = new Set(schedules.map((s) => s.type));
        return enabledModules.filter((key) => !scheduled.has(key));
    }, [isV2, enabledModules, schedules]);

    const economicStatus = useMemo(() => {
        if (!economicConnected) {
            return {
                label: t("common.settingsOverview.notConnected"),
                variant: "secondary" as const,
            };
        }
        if (isV2 && incompleteSections.length > 0) {
            return {
                label: t("common.settingsOverview.sectionsIncomplete", {count: incompleteSections.length}),
                variant: "warning" as const,
            };
        }
        return {
            label: t("common.settingsOverview.ready"),
            variant: "success" as const,
        };
    }, [economicConnected, incompleteSections.length, isV2, t]);

    const scheduleStatus = useMemo(() => {
        if (scheduleCount === 0) {
            return {
                label: t("common.settingsOverview.noSchedules"),
                variant: "secondary" as const,
            };
        }
        if (isV2 && missingScheduleModules.length > 0) {
            return {
                label: t("common.settingsOverview.schedulesMissing", {count: missingScheduleModules.length}),
                variant: "warning" as const,
            };
        }
        return {
            label: t("common.settingsOverview.scheduleCount", {count: scheduleCount}),
            variant: "success" as const,
        };
    }, [scheduleCount, isV2, missingScheduleModules.length, t]);

    const firstIncomplete = incompleteSections[0];
    const economicFixPath = firstIncomplete
        ? `${getSettingsEconomicPath()}?setup=${firstIncomplete}`
        : getSettingsEconomicPath();

    return (
        <React.Fragment>
            <QLoading loading={loading}/>
            <div className="row">
                <SettingsPageHeader
                    title={t("common.settingsOverview.title")}
                    subtitle={t("common.settingsOverview.subtitle")}
                    showBackToDashboard={isV2}
                    actions={isV2 ? (
                        <div className="d-flex align-items-center gap-2">
                            <Link to={RouteDefine.help} className="btn btn-sm btn-light">
                                {t("common.settingsOverview.openHelp")}
                            </Link>
                            <span className="badge bg-primary align-self-center">
                                {t("common.settingsOverview.engineV2")}
                            </span>
                        </div>
                    ) : undefined}
                />
            </div>

            {isV2 && !loading && (incompleteSections.length > 0 || missingScheduleModules.length > 0) ? (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="alert alert-warning mb-0 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2" role="status">
                            <div>
                                <strong>{t("common.settingsOverview.healthTitle")}</strong>
                                {incompleteSections.length > 0 ? (
                                    <div className="small mt-1">
                                        {t("common.settingsOverview.healthIncomplete", {
                                            sections: incompleteSections.map(sectionLabel).join(", "),
                                        })}
                                    </div>
                                ) : null}
                                {missingScheduleModules.length > 0 ? (
                                    <div className="small mt-1">
                                        {t("common.settingsOverview.healthMissingSchedules", {
                                            modules: missingScheduleModules.map(moduleLabel).join(", "),
                                        })}
                                    </div>
                                ) : null}
                            </div>
                            {incompleteSections.length > 0 ? (
                                <Link to={economicFixPath} className="btn btn-sm btn-primary flex-shrink-0">
                                    {t("common.settingsOverview.fixFirst")}
                                </Link>
                            ) : (
                                <Link to={RouteDefine.settingsSchedule} className="btn btn-sm btn-primary flex-shrink-0">
                                    {t("common.settingsOverview.manageSchedules")}
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}

            <div className="row">
                <SettingsNavCard
                    title={t("common.leftSidebarO1.eConomic")}
                    description={t("common.settingsOverview.economicHelp")}
                    to={getSettingsEconomicPath()}
                    statusLabel={economicStatus.label}
                    statusVariant={economicStatus.variant}
                />
                <SettingsNavCard
                    title={t("common.leftSidebarO1.stripe")}
                    description={t("common.settingsOverview.stripeHelp")}
                    to={RouteDefine.settingsStripe}
                    statusLabel={stripeConnected
                        ? t("common.settingsOverview.connected")
                        : t("common.settingsOverview.notConnected")}
                    statusVariant={stripeConnected ? "success" : "secondary"}
                />
                <SettingsNavCard
                    title={t("common.leftSidebarO1.businessInfo")}
                    description={t("common.settingsOverview.businessHelp")}
                    to={RouteDefine.settingsBusiness}
                    statusLabel={profileComplete
                        ? t("common.settingsOverview.ready")
                        : t("common.settingsOverview.needsAttention")}
                    statusVariant={profileComplete ? "success" : "warning"}
                />
                <SettingsNavCard
                    title={t("common.leftSidebarO1.schedule")}
                    description={t("common.settingsOverview.scheduleHelp")}
                    to={RouteDefine.settingsSchedule}
                    statusLabel={scheduleStatus.label}
                    statusVariant={scheduleStatus.variant}
                />
                {isV2 ? (
                    <SettingsNavCard
                        title={t("common.settingsOverview.helpCardTitle")}
                        description={t("common.settingsOverview.helpCardHelp")}
                        to={RouteDefine.help}
                        statusLabel={t("common.settingsOverview.helpCardStatus")}
                        statusVariant="secondary"
                    />
                ) : null}
            </div>
        </React.Fragment>
    );
};

export default React.memo(SettingsOverview);
